import { useMemo } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  formatAwsDateString,
  formatCurrency,
  formatTimestamp,
} from "@covertree/utils";

import { Coverage, CoverageEventType, PolicyType } from "@graphql/api";
import { mapCoverageStatus, mapInsuranceCarrierName } from "@utils";

import PropertiesGrid from "@covertree/ui/content/PropertiesGrid";
import CoverageHomeDetails from "../../../../../../../../../components/CoverageHomeDetails";

import { PolicyEventsList } from "@routing/Residents/ResidentsContent/ResidentDetails/ResidentDetailsContent/ResidentDetailsTabs/content/CoveragesTabContent/CoverageItem/content/CoverageItemContent/components/PolicyEventsList";

interface Props {
  coverage: Coverage;
  switchToDocuments(): void;
}

export default function CoverageItemContent({
  coverage,
  switchToDocuments,
}: Props) {
  const policyStatus = useMemo(() => {
    const status = mapCoverageStatus(coverage.isActive);
    if (
      !coverage.isActive ||
      coverage.policyType !== PolicyType.External ||
      !coverage.events
    ) {
      return status;
    }

    const [lastEvent] = coverage.events;

    if (lastEvent.type === CoverageEventType.NonRenewal) {
      return `${status} (Non-Renewal issued)`;
    }

    if (lastEvent.type === CoverageEventType.Cancellation) {
      return `${status} (Cancellation issued)`;
    }

    return status;
  }, [coverage]);

  return (
    <Box>
      <PropertiesGrid
        properties={[
          {
            label: "Policy Type",
            value: coverage.policyType,
          },
          {
            label: "Insurance Carrier",
            value: mapInsuranceCarrierName(coverage.insuranceCarrierName),
          },
          {
            label: "Policy Status",
            value: policyStatus,
            color: "secondary.main",
          },
          {
            label: "Description",
            value: coverage.policyDescription ?? "N/A",
          },
        ]}
      />
      <PropertiesGrid
        properties={[
          {
            label: "Policy Term",
            value: `${formatAwsDateString(coverage.policyEffectiveDate)}${
              coverage.policyEndDate
                ? ` - ${formatAwsDateString(coverage.policyEndDate)}`
                : ""
            }`,
          },
          {
            label: "Policy ID",
            value: coverage.policyId,
          },
          {
            label: "Annual Cost",
            value: formatCurrency(coverage.annualCost),
          },
          {
            label: "Agency Name",
            value: coverage.agencyName || "N/A",
          },
          {
            label: "Agency Address",
            value: coverage.agencyAddress || "N/A",
          },
          {
            label: "Agency Phone Number",
            value: coverage.agencyPhoneNumber || "N/A",
          },
          {
            label: "Agency Email",
            value: coverage.agencyEmail || "N/A",
          },
        ]}
      />
      <PropertiesGrid
        properties={[
          ...(coverage.policyType === PolicyType.HomeOwners
            ? [
                {
                  label: "Created Date",
                  value: formatTimestamp(coverage.createdAt),
                },
              ]
            : []),
          {
            label: "Last Updated Date",
            value: formatTimestamp(coverage.updatedAt),
          },
          {
            label: "Last Updated By",
            value: coverage.updatedBy ?? "N/A",
          },
        ]}
      />
      <CoverageHomeDetails coverage={coverage} />
      {coverage.policyType === PolicyType.External ? (
        <Box px={2} mt={5} pb={4} data-testid="policy-events-section">
          <Typography variant="h5">Policy Events</Typography>
          <Box mt={2}>
            <PolicyEventsList
              coverageId={coverage.id}
              events={coverage.events}
            />
          </Box>
        </Box>
      ) : undefined}
      <Box px={2} mt={5} pb={4}>
        <Typography variant="h5">Documents</Typography>
        <Stack mt={2} direction="row" spacing={1} alignItems="center">
          <Typography color="text.disabled">
            Go to the documents section to see all coverage documents.{" "}
          </Typography>
          <Button
            variant="text"
            onClick={switchToDocuments}
            sx={{
              padding: 0,
              height: "auto",
              "&:hover": { background: "none" },
            }}>
            See All Documents
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
